export type OrganizationSettingsPayload = {
  id: string;
  name: string;
  slug: string;
  caseCodeLabel?: string | null;
  caseCodePrefix?: string | null;
  caseCodeStartNumber?: number | null;
  orgGroupActivatedAt?: string | null;
  orgGroupDeactivatedAt?: string | null;
  countryCode?: string | null;
  locale?: string | null;
  currency?: string | null;
  timezone?: string | null;
  isActive: boolean;
  orgGroup?: {
    id: string;
    name: string;
    slug: string;
    ownerOrganizationId?: string | null;
    isActive: boolean;
    unitCount: number;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  latestJoinRequest?: {
    id: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
    targetOrgGroupSlug: string;
    targetOrgGroupName?: string | null;
    decisionNotes?: string | null;
    requestedAt?: string;
    decidedAt?: string | null;
  } | null;
  createdAt?: string;
  updatedAt?: string;
};

export type OrgGroupJoinRequestPayload = {
  id: string;
  organizationId: string;
  targetOrgGroupId?: string | null;
  targetOrgGroupSlug: string;
  targetOrgGroupName?: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  decisionNotes?: string | null;
  requestedAt: string;
  decidedAt?: string | null;
  organization?: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
  };
  requestedByUser?: {
    id: string;
    username: string;
    fullName?: string | null;
    email?: string | null;
  } | null;
};

export type AccountProfileLinkedContact = {
  accessId: string;
  customerId: string;
  customerName: string;
  customerIsActive: boolean;
  contactId: string;
  locationId?: string | null;
  locationName?: string | null;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  isPrimary: boolean;
  isActive: boolean;
};

export type AccountProfilePayload = {
  userId: string;
  username: string;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  linkedContacts: AccountProfileLinkedContact[];
  updatesApplyToAllLinkedContacts: boolean;
  fieldConsistency: {
    fullName: boolean;
    email: boolean;
    phone: boolean;
    role: boolean;
  };
};

export type KnowledgeGraphAccessLevel = 'FULL' | 'UPLOAD_ONLY' | 'EDIT_ONLY' | 'READ_ONLY';

export type SettingsPayload = {
  global: {
    currency: string;
    units: string;
    dateFormat: string;
  };
  user: {
    currency?: string;
    units?: string;
    dateFormat?: string;
  };
  organization?: OrganizationSettingsPayload | null;
  pendingJoinRequests?: OrgGroupJoinRequestPayload[];
  globalOrgUnits?: Array<{
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    countryCode?: string | null;
    locale?: string | null;
    timezone?: string | null;
    orgGroupActivatedAt?: string | null;
    orgGroupDeactivatedAt?: string | null;
    knowledgeGraphAccessLevel?: KnowledgeGraphAccessLevel;
    knowledgeGraphAccessPolicy?: {
      id: string;
      orgGroupId: string;
      ownerOrganizationId: string;
      unitOrganizationId: string;
      accessLevel: KnowledgeGraphAccessLevel;
      notes?: string | null;
      updatedByUserId?: string | null;
      createdAt?: string | null;
      updatedAt?: string | null;
    } | null;
    createdAt?: string;
    updatedAt?: string;
    _count?: {
      customers: number;
      devices: number;
      cases: number;
    };
  }>;
  canManageGlobalOrg?: boolean;
  effective: {
    currency: string;
    units: string;
    dateFormat: string;
  };
};

export type LeaveTypeKey = 'ANNUAL' | 'SICK' | 'OTHER';
export type LeaveRolloverMode = 'ALL' | 'PARTIAL' | 'NONE';
export type AvailabilityBlockType = 'LEAVE' | 'SICK' | 'DOWN_DAY' | 'TRAINING' | 'ADMIN';

export type AvailabilityDayPayload = {
  key: string;
  label: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
};

export type LeaveAllowancePayload = {
  allowed: string;
  used: string;
  rolloverMode: LeaveRolloverMode;
  rolloverQty: string;
  notes?: string;
};

export type LeaveHistoryEntryPayload = {
  id: string;
  leaveType: LeaveTypeKey;
  startDate: string;
  endDate: string;
  days: string;
  status: 'APPROVED' | 'DENIED' | 'PENDING';
  note?: string;
  requestedAt?: string;
  decidedAt?: string;
  decidedByName?: string;
  decisionNote?: string;
};

export type AccountLeavePayload = {
  availability: {
    generalHoursEnabled: boolean;
    days: AvailabilityDayPayload[];
    leavePolicy: {
      annual: LeaveAllowancePayload;
      sick: LeaveAllowancePayload;
      other: LeaveAllowancePayload;
    };
    leaveHistory: LeaveHistoryEntryPayload[];
    calendarMonth: string;
    calendarView: 'MONTH' | 'QUARTER';
    calendarMode: AvailabilityBlockType;
    calendarBlocksByDate: Record<string, AvailabilityBlockType>;
  };
  summary: Record<
    LeaveTypeKey,
    {
      allowed: number;
      used: number;
      remaining: number;
      pending: number;
    }
  >;
};

export type CreateGroupUnitInput = {
  name: string;
  slug: string;
  groupSlug?: string;
  countryCode?: string;
  locale?: string;
  timezone?: string;
  adminUsername?: string;
  adminPassword?: string;
  adminFullName?: string;
  adminEmail?: string;
};

export async function fetchSettings(baseUrl: string, userId?: string): Promise<SettingsPayload> {
  const url = new URL(`${baseUrl}/api/settings`);
  if (userId) url.searchParams.set('userId', userId);
  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error('Failed to load settings');
  return resp.json();
}

export async function updateGlobalSettings(
  baseUrl: string,
  data: Partial<{ currency: string; units: string; dateFormat: string }>
): Promise<SettingsPayload['global']> {
  const resp = await fetch(`${baseUrl}/api/settings/global`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update global settings');
  return payload?.global;
}

export async function updateUserSettings(
  baseUrl: string,
  userId: string,
  data: Partial<{ currency: string | null; units: string | null; dateFormat: string | null }>
): Promise<SettingsPayload['user']> {
  const resp = await fetch(`${baseUrl}/api/settings/user`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...data })
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update user settings');
  return payload?.user;
}

export async function fetchOwnAccountProfile(baseUrl: string): Promise<AccountProfilePayload> {
  const resp = await fetch(`${baseUrl}/api/settings/profile`);
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to load account profile');
  return payload?.profile;
}

export async function fetchOwnLeaveSettings(baseUrl: string): Promise<AccountLeavePayload> {
  const resp = await fetch(`${baseUrl}/api/settings/leave`);
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to load leave data');
  return payload?.leave;
}

export async function requestOwnLeave(
  baseUrl: string,
  data: {
    leaveType: LeaveTypeKey;
    startDate: string;
    endDate: string;
    requestedDays: number;
    note?: string | null;
  }
): Promise<AccountLeavePayload> {
  const resp = await fetch(`${baseUrl}/api/settings/leave/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data || {})
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to submit leave request');
  return payload?.leave;
}

export async function updateOwnAccountProfile(
  baseUrl: string,
  data: {
    fullName: string;
    email: string;
    phone?: string | null;
    role?: string | null;
    confirmEmail?: string | null;
    currentPassword?: string | null;
  }
): Promise<AccountProfilePayload> {
  const resp = await fetch(`${baseUrl}/api/settings/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update account profile');
  return payload?.profile;
}

export async function updateOrganizationSettings(
  baseUrl: string,
  data: Partial<{
    name: string;
    slug: string;
    caseCodeLabel: string;
    caseCodePrefix: string;
    caseCodeStartNumber: number;
    countryCode: string;
    locale: string;
    currency: string;
    timezone: string;
    isActive: boolean;
  }>
): Promise<OrganizationSettingsPayload> {
  const resp = await fetch(`${baseUrl}/api/settings/organization`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update organization settings');
  return payload?.organization;
}

export async function updateOrganizationGroupSettings(
  baseUrl: string,
  data: Partial<{
    groupSlug: string;
    groupName: string;
    clearGroup: boolean;
  }>
): Promise<OrganizationSettingsPayload> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update global org settings');
  return payload?.organization;
}

export async function requestOrganizationGroupJoin(
  baseUrl: string,
  data: {
    groupSlug: string;
    groupName?: string;
  }
): Promise<{ request: OrgGroupJoinRequestPayload; organization: OrganizationSettingsPayload }> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/request`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to submit join request');
  return { request: payload?.request, organization: payload?.organization };
}

export async function decideOrganizationGroupJoinRequest(
  baseUrl: string,
  requestId: string,
  data: {
    decision: 'APPROVED' | 'REJECTED';
    decisionNotes?: string;
  }
): Promise<OrgGroupJoinRequestPayload> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/requests/${encodeURIComponent(requestId)}/decision`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to decide join request');
  return payload?.request;
}

export async function createOrganizationGroupUnit(
  baseUrl: string,
  data: CreateGroupUnitInput
): Promise<{
  id: string;
  name: string;
  slug: string;
  orgGroupId?: string | null;
  orgGroupActivatedAt?: string | null;
  orgGroupDeactivatedAt?: string | null;
  isActive: boolean;
}> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/units`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to create unit');
  return payload?.unit;
}

export async function updateOrganizationGroupUnitMembership(
  baseUrl: string,
  unitOrgId: string,
  active: boolean
): Promise<{
  id: string;
  name: string;
  slug: string;
  orgGroupActivatedAt?: string | null;
  orgGroupDeactivatedAt?: string | null;
  isActive: boolean;
}> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/units/${encodeURIComponent(unitOrgId)}/membership`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active })
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update unit membership');
  return payload?.unit;
}

export async function updateOrganizationGroupUnitKnowledgeGraphAccess(
  baseUrl: string,
  unitOrgId: string,
  accessLevel: KnowledgeGraphAccessLevel
): Promise<NonNullable<SettingsPayload['globalOrgUnits']>[number]> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/units/${encodeURIComponent(unitOrgId)}/knowledge-graph-access`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessLevel })
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update unit Knowledge Graph access');
  return payload?.unit;
}

export async function updateOrganizationGroupUnit(
  baseUrl: string,
  unitOrgId: string,
  data: Partial<{
    name: string;
    slug: string;
    countryCode: string;
    locale: string;
    timezone: string;
  }>
): Promise<{
  id: string;
  name: string;
  slug: string;
  countryCode?: string | null;
  locale?: string | null;
  timezone?: string | null;
  orgGroupActivatedAt?: string | null;
  orgGroupDeactivatedAt?: string | null;
  isActive: boolean;
}> {
  const resp = await fetch(`${baseUrl}/api/settings/organization/group/units/${encodeURIComponent(unitOrgId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const payload = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(payload?.error || 'Failed to update unit');
  return payload?.unit;
}
